/**
 * 1.6.5 — one press is not an accident (D-1.6.5-1; shared/press.ts has the rules in words). What a visit's card
 * shows after a press from the crew phone, for everyone looking at that card:
 *
 *   "Arrived 9:38 · Ana"  (the one who pressed)  /  "Ana marked this Arrived at 9:38"  (anyone else)   + Undo
 *   "Completed 9:01 · Ana"  /  "Ana marked this Completed at 9:01"                                       + Undo
 *   "Close out 703 first."  in place of the Arrive button, with the way to 703's close-out
 *   "Open 2 h 10 min — still there?"
 *   "Crew A" / "Crew B" on a day with more than one crew at work
 *   1.7.8: "Started 2:30 · Ana · recorded 2:50" when the time was entered later than it happened
 *
 * Every line is words with an icon — never colour alone. Undo asks why in one small dialog ("Pressed by mistake"
 * already chosen) and is gone when the minutes are over; the office clears a stamp from Office action after that.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TodayVisit } from '@shared/api-types';
import { startsVisit } from '@shared/arrival';
import { UNDO_REASONS, openTooLong, openTooLongMinutes, openWords, pressName, undoMinutes, undoOpen, type UndoReason } from '@shared/press';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { formatClockOf } from '@/lib/dates';
import { useDayPlan } from '@/lib/queries';
import { useAnnounce } from '@/components/Announcer';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Icon } from '@/components/Icons';

export type StampKind = 'arrived' | 'completed';

/** The two settings every role's card reads (they ride with the Day plan settings). */
export function usePressSettings(): { undo: number; tooLong: number } {
  const s = useDayPlan().data?.settings;
  return { undo: undoMinutes(s), tooLong: openTooLongMinutes(s) };
}

/** The clock, re-read every `ms` — Undo leaves the card when its minutes are over, with nobody pressing anything. */
export function useNow(ms = 15_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), ms);
    return () => window.clearInterval(timer);
  }, [ms]);
  return now;
}

/** "Pressed by mistake" (already chosen) or another reason, and a few optional words. */
export function UndoFields({ reason, words, onReason, onWords }: { reason: UndoReason; words: string; onReason: (r: UndoReason) => void; onWords: (w: string) => void }) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <>
      <fieldset className="field press-why">
        <legend>{t('press165.why')}</legend>
        {UNDO_REASONS.map((r) => (
          <label key={r} data-testid={`undo-reason-${r}`}>
            <input type="radio" name={`${id}-why`} checked={reason === r} onChange={() => onReason(r)} /> <span>{t(`press165.reason.${r}`)}</span>
          </label>
        ))}
      </fieldset>
      <div className="field">
        <label htmlFor={`${id}-words`}>{t('press165.words')}</label>
        <textarea id={`${id}-words`} className="input" maxLength={500} rows={2} value={words} onChange={(e) => onWords(e.target.value)} data-testid="undo-words" />
      </div>
    </>
  );
}

/** POSTs the undo and says what happened; the lists that show the visit are read again either way. */
export function useUndoPress() {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  return async (kind: StampKind, v: Pick<TodayVisit, 'appointmentId' | 'unit'>, reason: UndoReason, words: string, staff = false): Promise<string | null> => {
    const refresh = () => {
      for (const k of ['today', 'attention', 'appointments', 'planning']) void qc.invalidateQueries({ queryKey: [k] });
    };
    try {
      const body = JSON.stringify({ appointmentId: v.appointmentId, reason, words: words.trim() || undefined });
      await apiFetch(kind === 'arrived' ? '/api/housekeeping/arrived/undo' : `/api/appointments/${encodeURIComponent(v.appointmentId)}/mark-completed/undo`, { method: 'POST', body });
      const msg = t(staff ? (kind === 'arrived' ? 'card168.undoneStarted' : 'card168.undoneEnded') : kind === 'arrived' ? 'press165.undoneArrived' : 'press165.undoneCompleted', { unit: v.unit });
      toast(msg);
      announce(msg);
      refresh();
      return null;
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
      announce(m, 'assertive');
      refresh();
      return m;
    }
  };
}

/** Undo, asked once: why (already answered "pressed by mistake"), then one press. */
function UndoDialog({ kind, v, onClose, staff = false }: { kind: StampKind; v: Pick<TodayVisit, 'appointmentId' | 'unit'>; onClose: () => void; staff?: boolean }) {
  const { t } = useTranslation();
  const undo = useUndoPress();
  const [reason, setReason] = useState<UndoReason>('mistake');
  const [words, setWords] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    setBusy(true);
    setError(null);
    const failed = await undo(kind, v, reason, words, staff);
    setBusy(false);
    if (failed) setError(failed);
    else onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="undo-dialog">
        <DialogTitle>{t(staff ? (kind === 'arrived' ? 'card168.undoTitleStarted' : 'card168.undoTitleEnded') : kind === 'arrived' ? 'press165.undoTitleArrived' : 'press165.undoTitleCompleted', { unit: v.unit })}</DialogTitle>
        <DialogDescription>{t(staff ? (kind === 'arrived' ? 'card168.undoLeadStarted' : 'card168.undoLeadEnded') : kind === 'arrived' ? 'press165.undoLeadArrived' : 'press165.undoLeadCompleted')}</DialogDescription>
        <UndoFields reason={reason} words={words} onReason={setReason} onWords={setWords} />
        {error ? (
          <div className="warn take" role="alert" data-testid="undo-error">
            <Icon name="alert" />
            <div>{error}</div>
          </div>
        ) : null}
        <div className="chips">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {t('press165.keep')}
          </Button>
          <Button type="button" onClick={() => go()} disabled={busy} data-testid="undo-submit">
            {t('press165.undo')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The stamp on a card: who pressed and when, with Undo while the minutes last. `mine`: the reader is the one who
 * pressed ("Arrived 9:38 · Ana"); anyone else reads "Ana marked this Arrived at 9:38".
 */
export function StampLine({ kind, v, by, at, me, canUndo, now, minutes, pre = '', staff = false, recordedAt }: { kind: StampKind; v: Pick<TodayVisit, 'appointmentId' | 'unit'>; by: string; at: string; me: string | null; canUndo: boolean; now: Date; minutes: number; pre?: string; staff?: boolean; /** 1.7.8: the time was entered later — " · recorded 2:50" */ recordedAt?: string }) {
  const { t, i18n } = useTranslation();
  const [asking, setAsking] = useState(false);
  const mine = !!me && me.toLowerCase() === by.toLowerCase();
  const words = { name: pressName(by), time: formatClockOf(at, i18n.language) };
  // 1.6.8: a staff unit or an office keeps two times — "Started 9:40 · Brigida", "Ended 11:05 · Brigida"
  const staffKey = kind === 'arrived' ? (mine ? 'card168.startedBy' : 'card168.markedStarted') : mine ? 'card168.endedBy' : 'card168.markedEnded';
  const key = staff ? staffKey : kind === 'arrived' ? (mine ? 'press165.arrivedBy' : 'press165.markedArrived') : mine ? 'press165.completedBy' : 'press165.markedCompleted';
  const undoFor = staff ? (kind === 'arrived' ? 'card168.undoStartedFor' : 'card168.undoEndedFor') : kind === 'arrived' ? 'press165.undoArrivedFor' : 'press165.undoCompletedFor';
  return (
    <p className="press-line" data-testid={`${pre}stamp-${kind}-${v.appointmentId}`}>
      <Icon name="check" /> <span>{t(key, words)}</span>
      {recordedAt ? <span data-testid={`${pre}recorded-at-${v.appointmentId}`}> · {t('bend178.recordedAt', { time: formatClockOf(recordedAt, i18n.language) })}</span> : null}
      {canUndo && undoOpen(at, minutes, now) ? (
        <Button size="sm" variant="secondary" aria-label={t(undoFor, { unit: v.unit })} onClick={() => setAsking(true)} data-testid={`${pre}undo-${kind}-${v.appointmentId}`}>
          {t('press165.undo')}
        </Button>
      ) : null}
      {asking ? <UndoDialog kind={kind} v={v} staff={staff} onClose={() => setAsking(false)} /> : null}
    </p>
  );
}

/** Everything 1.6.5 adds under a visit's status on Today — nothing at all for a visit nobody has pressed. */
export function PressLines({ v, day, me, canUndo, now, onCloseOut, pre = '', firstIsStaff = false }: { v: TodayVisit; day: string; me: string | null; canUndo: boolean; now: Date; onCloseOut?: (appointmentId: string, opener: HTMLElement) => void; pre?: string; /** 1.6.8: the visit to close first is a staff unit — "End 1207 first." */ firstIsStaff?: boolean }) {
  const { t } = useTranslation();
  const { undo, tooLong } = usePressSettings();
  const open = v.status === 'Scheduled' || v.status === 'In Progress';
  const a = v.arrival && v.arrival.dayKey === day ? v.arrival : null;
  const lines: ReactNode[] = [];
  // 1.6.8: a staff unit or an office — Start and End; the two times stay on the card once it has ended
  const staff = v.kind === 'staff' || v.kind === 'facility';
  const ended = staff && !!v.marked && v.status === 'Completed';
  // a visit that started: the stamp stays while it is open (closed out, the close-out's own line says who)
  if (a && (open || ended) && (startsVisit(a.answer) || a.answer === 'noAnswer')) {
    if (startsVisit(a.answer)) lines.push(<StampLine key="arrived" pre={pre} kind="arrived" staff={staff} v={v} by={a.by} at={a.at} me={me} canUndo={canUndo && !v.closeout && open} now={now} minutes={undo} recordedAt={a.recordedAt} />);
    // "not home — try again later": its own line says when; Undo sits beside it for the same minutes
    else if (canUndo && undoOpen(a.at, undo, now)) lines.push(<NotHomeUndo key="nothome" pre={pre} v={v} by={a.by} />);
    const long = v.closeout || !open ? null : openTooLong(a, v.lengthMinutes ?? 60, tooLong, now);
    if (long !== null)
      lines.push(
        <p key="long" className="press-line is-long" role="status" data-testid={`${pre}open-long-${v.appointmentId}`}>
          <Icon name="clock" /> <span>{t('press165.openTooLong', { time: openWords(long) })}</span>
        </p>,
      );
  }
  if (v.marked && v.status === 'Completed') lines.push(<StampLine key="completed" pre={pre} kind="completed" staff={staff} v={v} by={v.marked.by} at={v.marked.at} me={me} canUndo={canUndo} now={now} minutes={undo} />);
  if (v.closeOutFirst && v.status === 'Scheduled')
    lines.push(
      <p key="first" className="press-line is-first" data-testid={`close-out-first-${v.appointmentId}`}>
        <Icon name="lock" /> <span>{t(firstIsStaff ? 'card168.endFirst' : 'press165.closeOutFirst', { unit: v.closeOutFirst.unit })}</span>
        {onCloseOut ? (
          <Button size="sm" variant="secondary" onClick={(e) => onCloseOut(v.closeOutFirst!.appointmentId, e.currentTarget)} data-testid={`close-out-open-${v.appointmentId}`}>
            {t(firstIsStaff ? 'card168.endOpen' : 'press165.closeOutOpen', { unit: v.closeOutFirst.unit })}
          </Button>
        ) : null}
      </p>,
    );
  return lines.length ? <>{lines}</> : null;
}

function NotHomeUndo({ v, by, pre }: { v: Pick<TodayVisit, 'appointmentId' | 'unit'>; by: string; pre: string }) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  return (
    <p className="press-line" data-testid={`${pre}stamp-nothome-${v.appointmentId}`}>
      <Icon name="info" /> <span>{t('press165.notHomeBy', { name: pressName(by) })}</span>
      <Button size="sm" variant="secondary" aria-label={t('press165.undoArrivedFor', { unit: v.unit })} onClick={() => setAsking(true)} data-testid={`${pre}undo-arrived-${v.appointmentId}`}>
        {t('press165.undo')}
      </Button>
      {asking ? <UndoDialog kind="arrived" v={v} onClose={() => setAsking(false)} /> : null}
    </p>
  );
}

/** "Crew A" — words, on a day with more than one crew at work (the server sends the tag only then). */
export function CrewTag({ crew, testid }: { crew: 'A' | 'B' | null | undefined; testid?: string }) {
  const { t } = useTranslation();
  if (!crew) return null;
  return (
    <span className="status crew-tag has-icon" data-testid={testid}>
      <Icon name="people" />
      {t('press165.crewTag', { crew })}
    </span>
  );
}

/**
 * The one line that confirms a staff unit's press. 1.6.8: Start — "Confirm the visit at 1207 is starting now" — and
 * End, which is what Mark completed was ("Confirm the visit at 1207 has ended"). Yes / Not yet; nothing is sent by
 * the first tap.
 */
export function ConfirmCompleted({ unit, busy, onYes, onNo, what = 'end' }: { unit: string; busy?: boolean; onYes: () => void; onNo: () => void; what?: 'start' | 'end' }) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(o) => !o && onNo()}>
      <DialogContent data-testid={what === 'start' ? 'confirm-start' : 'confirm-completed'}>
        <DialogTitle>{t(what === 'start' ? 'card168.confirmStartTitle' : 'card168.confirmEndTitle', { unit })}</DialogTitle>
        <DialogDescription>{t(what === 'start' ? 'card168.confirmStartLine' : 'card168.confirmEndLine', { unit })}</DialogDescription>
        <div className="chips">
          <Button type="button" variant="secondary" onClick={onNo} disabled={busy} data-testid={what === 'start' ? 'confirm-start-no' : 'confirm-completed-no'}>
            {t('press165.notYet')}
          </Button>
          <Button type="button" onClick={onYes} disabled={busy} data-testid={what === 'start' ? 'confirm-start-yes' : 'confirm-completed-yes'}>
            {t(what === 'start' ? 'card168.yesStart' : 'card168.yesEnd')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
